/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export type ScrollAxis = 'vertical' | 'horizontal' | 'both'

type ScrollContainerProps = {
  children: React.ReactNode
  /** Which axis scrolls. Default: 'vertical'. */
  axis?: ScrollAxis
  /** Track thickness in px. Default: 6. */
  size?: number
  /**
   * Drive a scroller that a child already owns, instead of scrolling here.
   *
   * Some components must keep their own scroll element — antd's `<Table
   * scroll={{ x }}` scrolls `.ant-table-content` itself, and that is what
   * makes `fixed` columns stick. Wrapping such a table in a second scroller
   * breaks the sticky columns. Pass the child scroller's selector and the
   * thumb reads from and writes to *that* element; this container then only
   * hosts the track and does not scroll.
   */
  targetSelector?: string
  /**
   * Reserve the same gutter on the LEFT that the vertical track reserves on the
   * right, so content sits centred rather than shifted left by the track.
   * Vertical, self-scrolling containers only. Default: false.
   */
  mirrorTrackGutter?: boolean
  /** Extra emotion styles for the viewport (sizing, padding, …). */
  className?: string
  /**
   * Pin the horizontal track to the bottom of the nearest scrolling ancestor
   * — the window in normal page-scroll mode, a scrolling ancestor column in
   * full-screen mode — instead of the container's own bottom edge. Without
   * this, a horizontal scrollbar on a very tall container sits at the
   * container's bottom, thousands of px down and unreachable by mouse.
   *
   * CSS `position: sticky` resolves against whichever is the nearest ancestor
   * that clips overflow on that axis; nothing here has to know which one that
   * is. Default `false` — every existing caller renders exactly as before.
   */
  stickyTrack?: boolean
  /**
   * Reserve room beside the content for each track (`size` plus an 8px gap:
   * below it for the horizontal track, to its right for the vertical one), so
   * content never sits under a track. Default `true` — every existing caller
   * renders exactly as before.
   *
   * `false` reserves nothing: the content keeps the container's full size and
   * the track overlays its bottom (or right) edge. For a fixed-height strip —
   * a 48px bar whose content must center in that height — where the reserved
   * gutter would push the content off center. Both modes honor it: the
   * viewport's padding when it scrolls itself, and its margin when a child
   * owns the scroller (`targetSelector`). With `stickyTrack`, the track is an
   * in-flow sibling below the viewport and keeps its own height; only the
   * reserved gutter is dropped.
   */
  reserveTrackSpace?: boolean
  /**
   * With `stickyTrack`, give the track a backdrop while it is STUCK — held
   * against a scrolling ancestor's edge, floating over content — and drop it
   * once the container has scrolled far enough that the track rests at its own
   * bottom. A bare 6px thumb over table rows reads as unrelated to them; a
   * short panel behind it ties it to the grid it scrolls.
   *
   * Requires `stickyTrack` (nothing to detect without it) and
   * `reserveTrackSpace` (the panel fills the reserved gutter; with no gutter
   * it would paint over content, so it is ignored). Default `false`, so every
   * existing caller renders exactly as before.
   */
  trackBackdrop?: boolean
  /**
   * When the track is drawn. Default `'always'`.
   *
   * `'hover'` fades it in while the pointer is over the container (or the
   * thumb has focus) and out again on leave. For a strip repeated many times
   * down the page — one track per copy reads as a row divider and buries the
   * one scrollbar that matters — where the content still has to be reachable,
   * so hiding it outright would be a trap. At most one track shows at a time,
   * and it appears exactly where the user is already pointing.
   *
   * Keyboard and touch never hover: the track also shows while the thumb has
   * focus, and coarse pointers (no hover at all) fall back to `'always'` via a
   * media query, so a touch user is never left without an affordance.
   */
  trackVisibility?: 'always' | 'hover'
}

/** Minimum thumb length so it stays grabbable on very long content. */
const MIN_THUMB_PX = 32

/** Inset at each end of the track, so it does not run flush to the edges. */
const TRACK_INSET_PX = 8

/** Default track thickness. Slim enough to read as chrome, not as content. */
const DEFAULT_SIZE_PX = 6

/** Gap between the scrolled content and the track, so they never crowd. */
const TRACK_GAP_PX = 8

type Metrics = {
  /** Thumb length as a fraction of the track (0–1). */
  ratio: number
  /** Thumb offset as a fraction of the track (0–1). */
  offset: number
  /** Whether this axis currently overflows. */
  overflows: boolean
}

const EMPTY_METRICS: Metrics = { ratio: 0, offset: 0, overflows: false }

/**
 * Scroll area with a **custom-drawn** scrollbar that is always visible.
 *
 * Native scrollbars cannot be relied on here: macOS defaults to "Show scroll
 * bars: When scrolling", which hides them entirely at rest, and
 * `::-webkit-scrollbar` styling is ignored under some OS/browser combinations
 * (and unsupported in Firefox, which only offers `scrollbar-width`). So the
 * native bar is hidden outright and the thumb below is a real DOM element,
 * positioned from `scrollTop`/`scrollHeight`. That renders identically on every
 * browser and OS setting.
 *
 * The thumb is draggable, and the track is click-to-page. Reusable — not
 * feature-specific. Lives in `src/components` rather than `common/` because it
 * imports React, and `common/` is framework-agnostic.
 *
 * @example
 * <ScrollContainer axis='both' css={css`max-height: 400px;`}>
 *   <WideTable />
 * </ScrollContainer>
 *
 * @example Driving a scroller the child owns (keeps antd's fixed columns):
 * <ScrollContainer axis='horizontal' targetSelector='.ant-table-content'>
 *   <Table scroll={{ x: 'max-content' }} />
 * </ScrollContainer>
 *
 * @example A fixed-height strip, with `reserveTrackSpace={false}` so no gutter
 * pushes its content off center (the track overlays the bottom edge instead):
 * <ScrollContainer axis='horizontal' reserveTrackSpace={false}>
 *   <StatsStrip />
 * </ScrollContainer>
 */
export const ScrollContainer = React.forwardRef<
  HTMLDivElement,
  ScrollContainerProps
>(
  (
    {
      children,
      axis = 'vertical',
      size = DEFAULT_SIZE_PX,
      targetSelector,
      mirrorTrackGutter = false,
      className,
      stickyTrack = false,
      reserveTrackSpace = true,
      trackBackdrop = false,
      trackVisibility = 'always',
    },
    forwardedRef
  ) => {
    const viewportRef = useRef<HTMLDivElement>(null)

    /** The element that actually scrolls: our viewport, or the child's. */
    const getScroller = useCallback((): HTMLElement | null => {
      const viewport = viewportRef.current
      if (!viewport) {
        return null
      }
      return targetSelector
        ? viewport.querySelector<HTMLElement>(targetSelector)
        : viewport
    }, [targetSelector])

    useImperativeHandle(
      forwardedRef,
      () => viewportRef.current as HTMLDivElement
    )

    const [vertical, setVertical] = useState<Metrics>(EMPTY_METRICS)
    const [horizontal, setHorizontal] = useState<Metrics>(EMPTY_METRICS)

    /**
     * Whether the sticky track is currently held away from its resting place.
     * Only meaningful with `stickyTrack` and `trackBackdrop`.
     */
    const [isStuck, setIsStuck] = useState(false)

    const showY = axis === 'vertical' || axis === 'both'
    const showX = axis === 'horizontal' || axis === 'both'

    const measure = useCallback(() => {
      const el = getScroller()
      if (!el) {
        return
      }

      const { scrollTop, scrollHeight, clientHeight } = el
      const { scrollLeft, scrollWidth, clientWidth } = el

      setVertical(
        scrollHeight > clientHeight
          ? {
              ratio: clientHeight / scrollHeight,
              offset: scrollTop / scrollHeight,
              overflows: true,
            }
          : EMPTY_METRICS
      )

      setHorizontal(
        scrollWidth > clientWidth
          ? {
              ratio: clientWidth / scrollWidth,
              offset: scrollLeft / scrollWidth,
              overflows: true,
            }
          : EMPTY_METRICS
      )
    }, [getScroller])

    // Measure on mount and whenever the content or viewport resizes. A plain
    // effect would miss content that grows later (async fields, expanded rows).
    useLayoutEffect(() => {
      const viewport = viewportRef.current
      if (!viewport) {
        return
      }
      measure()

      if (typeof ResizeObserver === 'undefined') {
        return
      }
      const observer = new ResizeObserver(measure)
      observer.observe(viewport)
      // Observe the content too — the viewport itself may never change size.
      Array.from(viewport.children).forEach((child) => observer.observe(child))

      // With a child scroller, the width that overflows lives deeper still
      // (antd's inner <table>), so observe the scroller and its content too.
      const scroller = getScroller()
      if (scroller && scroller !== viewport) {
        observer.observe(scroller)
        Array.from(scroller.children).forEach((child) =>
          observer.observe(child)
        )
      }
      return () => observer.disconnect()
    }, [measure, getScroller, children])

    // A child-owned scroller emits scroll on itself, not on our viewport, and
    // React's onScroll would not see it (scroll does not bubble). Subscribe
    // directly instead.
    useEffect(() => {
      const scroller = getScroller()
      if (!scroller || scroller === viewportRef.current) {
        return
      }
      scroller.addEventListener('scroll', measure, { passive: true })
      return () => scroller.removeEventListener('scroll', measure)
    }, [getScroller, measure, children])

    /*
     * "Stuck" cannot be read from CSS — `position: sticky` exposes no state —
     * so a sentinel marks where the track comes to rest and an
     * IntersectionObserver watches whether that point is on screen.
     *
     * The sentinel sits AFTER the track in flow, at the container's bottom
     * edge. IntersectionObserver does no occlusion testing, so this is not
     * "the track covers the sentinel": it is simply "is the container's bottom
     * edge in view". That is exactly the condition wanted — the track rests
     * there, so a bottom edge out of view means the track is being held
     * somewhere above it, and a bottom edge in view means it has come to rest.
     * Watching this rather than the track itself avoids the usual `-1px`
     * root-margin trick, which needs to know WHICH ancestor scrolls — the
     * thing `stickyTrack` deliberately leaves to CSS.
     *
     * It is 1px tall, not zero: a zero-area target's intersection is not
     * treated consistently across engines (WebKit has reported
     * `isIntersecting: false` for them even when in view), which would pin the
     * backdrop on forever. The 1px is absorbed by a negative margin so it
     * still adds nothing to the container's height.
     */
    const watchStuck =
      trackBackdrop && stickyTrack && showX && reserveTrackSpace

    /*
     * A callback ref, not `useEffect` + `useRef`: the sentinel mounts and
     * unmounts with the overflow branch, and a ref object would leave the
     * effect observing a detached node (or nothing at all) on the render where
     * that branch flips. This re-runs exactly when the node appears or goes.
     */
    const observerRef = useRef<IntersectionObserver | null>(null)
    const setSentinel = useCallback((node: HTMLDivElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null

      if (!node || typeof IntersectionObserver === 'undefined') {
        // No way to observe: rest state, so the track never sticks "on".
        setIsStuck(false)
        return
      }

      const observer = new IntersectionObserver(
        ([entry]) => setIsStuck(!entry.isIntersecting),
        { threshold: 0 }
      )
      observer.observe(node)
      observerRef.current = observer
    }, [])

    // The node outlives no unmount path other than the one above, but a
    // container torn down mid-scroll would otherwise leak its observer.
    useEffect(() => () => observerRef.current?.disconnect(), [])

    // Nothing to be stuck to once the axis stops overflowing.
    useEffect(() => {
      if (!watchStuck || !horizontal.overflows) {
        setIsStuck(false)
      }
    }, [watchStuck, horizontal.overflows])

    /** Drag the thumb; maps pointer delta back onto scrollTop/scrollLeft. */
    const startDrag = useCallback(
      (event: React.PointerEvent, isVertical: boolean) => {
        event.preventDefault()
        const el = getScroller()
        if (!el) {
          return
        }

        const startPos = isVertical ? event.clientY : event.clientX
        const startScroll = isVertical ? el.scrollTop : el.scrollLeft
        const trackPx = isVertical ? el.clientHeight : el.clientWidth
        const contentPx = isVertical ? el.scrollHeight : el.scrollWidth

        const onMove = (moveEvent: PointerEvent) => {
          const delta =
            (isVertical ? moveEvent.clientY : moveEvent.clientX) - startPos
          // Thumb travel : content travel is trackPx : contentPx
          const scrolled = startScroll + (delta * contentPx) / trackPx
          if (isVertical) {
            el.scrollTop = scrolled
          } else {
            el.scrollLeft = scrolled
          }
        }

        const onUp = () => {
          window.removeEventListener('pointermove', onMove)
          window.removeEventListener('pointerup', onUp)
        }

        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
      },
      [getScroller]
    )

    /** Click on empty track = page up/down (matches native behaviour). */
    const handleTrackClick = useCallback(
      (event: React.MouseEvent, isVertical: boolean) => {
        const el = getScroller()
        if (!el || event.target !== event.currentTarget) {
          return
        }
        const rect = event.currentTarget.getBoundingClientRect()
        const clickPos = isVertical
          ? event.clientY - rect.top
          : event.clientX - rect.left
        const thumbStart =
          (isVertical ? vertical.offset : horizontal.offset) *
          (isVertical ? rect.height : rect.width)
        const page = isVertical ? el.clientHeight : el.clientWidth
        const forward = clickPos > thumbStart

        if (isVertical) {
          el.scrollTop += forward ? page : -page
        } else {
          el.scrollLeft += forward ? page : -page
        }
      },
      [getScroller, vertical.offset, horizontal.offset]
    )

    const ownsScroll = !targetSelector

    return (
      <div
        css={rootStyle(stickyTrack, trackVisibility === 'hover')}
        className={className}
      >
        <div
          ref={viewportRef}
          css={viewportStyle(
            showY,
            showX,
            size,
            ownsScroll,
            reserveTrackSpace,
            mirrorTrackGutter
          )}
          onScroll={ownsScroll ? measure : undefined}
        >
          {children}
        </div>

        {showY && vertical.overflows ? (
          <div
            css={verticalTrackStyle(size, trackVisibility === 'hover')}
            onMouseDown={(e) => handleTrackClick(e, true)}
          >
            <div
              css={verticalThumbStyle(vertical, size)}
              onPointerDown={(e) => startDrag(e, true)}
            />
          </div>
        ) : null}

        {showX && horizontal.overflows ? (
          <>
            <div
              css={horizontalTrackStyle(
                size,
                stickyTrack,
                watchStuck && isStuck,
                trackVisibility === 'hover'
              )}
              onMouseDown={(e) => handleTrackClick(e, false)}
            >
              <div
                css={horizontalThumbStyle(
                  horizontal,
                  size,
                  watchStuck && isStuck
                )}
                onPointerDown={(e) => startDrag(e, false)}
              />
            </div>
            {watchStuck ? <div ref={setSentinel} css={sentinelStyle} /> : null}
          </>
        ) : null}
      </div>
    )
  }
)

ScrollContainer.displayName = 'ScrollContainer'

export default ScrollContainer

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * Set on the container and read by its tracks, so a hover-revealed track
 * needs no React state and no re-render per pointer move.
 */
const TRACK_OPACITY_VAR = '--sn-scroll-track-opacity'

/**
 * Fade the track in and out. Only ever applied when the caller asked for
 * `'hover'`, so an `'always'` container emits neither this nor the variable —
 * its rendered CSS is byte-for-byte what it was before the prop existed. The
 * `1` fallback keeps the track visible if the variable is ever missing.
 */
const trackReveal = `
  opacity: var(${TRACK_OPACITY_VAR}, 1);
  transition: opacity 0.15s ease;
`

const TRACK_COLOR = 'rgba(255, 255, 255, 0.06)'
const THUMB_COLOR = 'rgba(255, 255, 255, 0.30)'
const THUMB_HOVER_COLOR = 'rgba(255, 255, 255, 0.50)'

/**
 * Panel behind a stuck track, so it reads as part of the grid it scrolls.
 *
 * `SECONDARY_200` is the grid's own hover surface, so the panel reads as a
 * grid surface lifting off the rows rather than a new color. Deliberately NOT
 * `colors.PRIMARY`: a tenant fill puts the 30% white thumb at 1.19:1 on
 * SportNinja and 1.52:1 on Canlan — the thumb all but disappears on the very
 * control it belongs to. Neutral also means one appearance on both tenants.
 */
const BACKDROP_COLOR = colors.SECONDARY_200
const BACKDROP_BORDER = colors.SECONDARY_100

/**
 * The thumb over that panel. 30% white reads at 2.47:1 on `SECONDARY_200` —
 * below AA — so a stuck thumb lifts to 55% (4.70:1). Only while stuck: over
 * the page the default already has the contrast it needs.
 */
const STUCK_THUMB_COLOR = 'rgba(255, 255, 255, 0.55)'
const STUCK_THUMB_HOVER_COLOR = 'rgba(255, 255, 255, 0.72)'

/**
 * The groove itself, over that panel. 6% white lands at 1.20:1 on the lighter
 * panel — the groove disappears and with it the cue for how much scroll is
 * left, so a stuck track takes 12%.
 */
const STUCK_TRACK_COLOR = 'rgba(255, 255, 255, 0.12)'

/**
 * `flex-direction: column` only when `stickyTrack` is on: the sticky track
 * must be a normal in-flow sibling AFTER the viewport (see
 * `horizontalTrackStyle`) so it has a static position to rest at once scrolled
 * past, and column layout is what stacks it below the viewport instead of
 * beside it. Every existing caller leaves `stickyTrack` unset and keeps the
 * original row layout — there is still only ever one non-absolutely-positioned
 * child (the viewport) either way, so this does not change sizing.
 */
const rootStyle = (stickyTrack: boolean, hoverTrack: boolean) => css`
  position: relative;
  display: flex;
  ${stickyTrack ? 'flex-direction: column;' : ''}
  min-height: 0;
  min-width: 0;

  /*
   * The hover state is published as a CSS custom property rather than React
   * state: no re-render per pointer enter/leave, and the tracks below read it
   * straight from their parent. Coarse pointers never hover, so the fallback
   * further down pins it to 1 for them.
   */
  ${hoverTrack
    ? `
      ${TRACK_OPACITY_VAR}: 0;
      &:hover, &:focus-within {
        ${TRACK_OPACITY_VAR}: 1;
      }
      @media (hover: none) {
        ${TRACK_OPACITY_VAR}: 1;
      }
    `
    : ''}
`

/**
 * `reserveTrackSpace` off zeroes the reserved gutter on both axes and in both
 * modes; on, the CSS is exactly what it was before the prop existed.
 */
const viewportStyle = (
  showY: boolean,
  showX: boolean,
  size: number,
  ownsScroll: boolean,
  reserveTrackSpace: boolean,
  mirrorTrackGutter: boolean
) => css`
  flex: 1;
  min-height: 0;
  min-width: 0;

  ${ownsScroll
    ? `
      overflow-y: ${showY ? 'auto' : 'hidden'};
      overflow-x: ${showX ? 'auto' : 'hidden'};
      /* Leave room for the custom track so content never sits under it */
      padding-right: ${showY && reserveTrackSpace ? size + TRACK_GAP_PX : 0}px;
      padding-left: ${
        showY && reserveTrackSpace && mirrorTrackGutter
          ? size + TRACK_GAP_PX
          : 0
      }px;
      padding-bottom: ${showX && reserveTrackSpace ? size + TRACK_GAP_PX : 0}px;
    `
    : `
      /* A child owns the scroller; only reserve room for the track. */
      margin-bottom: ${showX && reserveTrackSpace ? size + TRACK_GAP_PX : 0}px;
      margin-right: ${showY && reserveTrackSpace ? size + TRACK_GAP_PX : 0}px;
    `}

  /*
   * Hide the NATIVE scrollbar on every engine — the custom thumb replaces it.
   * Applied to descendants too, so a child-owned scroller is covered as well.
   */
  &,
  & * {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* legacy Edge */
  }
  &::-webkit-scrollbar,
  & *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  /* Keep a scrolled-to field clear of the container's top edge */
  scroll-padding: 12px;
`

const verticalTrackStyle = (size: number, hoverTrack: boolean) => css`
  position: absolute;
  top: ${TRACK_INSET_PX}px;
  right: 0;
  bottom: ${TRACK_INSET_PX}px;
  width: ${size}px;
  background-color: ${TRACK_COLOR};
  border-radius: ${size}px;
  cursor: pointer;
  ${hoverTrack ? trackReveal : ''}
`

const verticalThumbStyle = (metrics: Metrics, size: number) => css`
  position: absolute;
  left: 0;
  width: ${size}px;
  height: max(${MIN_THUMB_PX}px, ${metrics.ratio * 100}%);
  top: ${metrics.offset * 100}%;
  background-color: ${THUMB_COLOR};
  border-radius: ${size}px;
  cursor: grab;
  transition: background-color 0.15s;
  &:hover {
    background-color: ${THUMB_HOVER_COLOR};
  }
  &:active {
    background-color: ${THUMB_HOVER_COLOR};
    cursor: grabbing;
  }
`

/**
 * Non-sticky (default): unchanged — absolutely positioned against `rootStyle`
 * (`position: relative`), insets via `left`/`right`, sitting at the bottom of
 * the container's own box.
 *
 * Sticky: `position: sticky` takes the element out of the absolute-positioning
 * scheme (the two are mutually exclusive) and back into normal flow, so it
 * needs `margin` instead of `left`/`right` for the same visual inset — for a
 * relatively/stickily positioned box, `left`/`right` are offsets from the
 * static position, not a stretch between two edges, so they cannot size the
 * box the way they do under `position: absolute`. Being a normal flow child
 * (see `rootStyle`) it still stretches to the container's width by default
 * flex `align-items: stretch`, so the margins reproduce the same 8px inset.
 * `bottom: 0` is then the sticky offset: stuck to the bottom of the nearest
 * ancestor that scrolls on this axis (the window, or a full-screen column)
 * for as long as any part of the container is on screen, and resting at the
 * container's own bottom — its normal static position, right after the
 * viewport — once scrolled past.
 */
const horizontalTrackStyle = (
  size: number,
  stickyTrack: boolean,
  stuck: boolean,
  hoverTrack: boolean
) => css`
  position: ${stickyTrack ? 'sticky' : 'absolute'};
  ${stickyTrack
    ? `margin: 0 ${TRACK_INSET_PX}px;`
    : `left: ${TRACK_INSET_PX}px; right: ${TRACK_INSET_PX}px;`}
  bottom: 0;
  height: ${size}px;
  background-color: ${stuck ? STUCK_TRACK_COLOR : TRACK_COLOR};
  border-radius: ${size}px;
  cursor: pointer;
  ${hoverTrack ? trackReveal : ''}

  /*
   * The backdrop is drawn with a box-shadow spread rather than padding or a
   * wrapper: the track's own box stays exactly the size the thumb is measured
   * against, so nothing about the drag math or the click-to-page arithmetic
   * changes when it appears. It is painted only while stuck, and fades rather
   * than popping as the container scrolls to its rest.
   *
   * The spread is what reserveTrackSpace already set aside, so the panel
   * fills the reserved gutter and no more. Without a reserved gutter there is
   * nothing to fill and painting one would cover content, so the backdrop is
   * simply not offered (see the trackBackdrop prop).
   */
  transition: box-shadow 0.15s ease;
  ${stuck
    ? `box-shadow:
         0 0 0 ${TRACK_GAP_PX}px ${BACKDROP_COLOR},
         0 -1px 0 ${TRACK_GAP_PX}px ${BACKDROP_BORDER};`
    : ''}
`

/**
 * Marker at the track's resting place, watched by an IntersectionObserver to
 * tell "stuck" from "resting". 1px rather than zero — a zero-area target's
 * intersection is not treated consistently across engines — with a matching
 * negative margin so it still adds nothing to the container's height. Never
 * painted and never hit-tested.
 */
const sentinelStyle = css`
  height: 1px;
  margin-top: -1px;
  pointer-events: none;
`

const horizontalThumbStyle = (
  metrics: Metrics,
  size: number,
  stuck: boolean
) => css`
  position: absolute;
  top: 0;
  height: ${size}px;
  width: max(${MIN_THUMB_PX}px, ${metrics.ratio * 100}%);
  left: ${metrics.offset * 100}%;
  background-color: ${stuck ? STUCK_THUMB_COLOR : THUMB_COLOR};
  border-radius: ${size}px;
  cursor: grab;
  transition: background-color 0.15s;
  &:hover {
    background-color: ${stuck ? STUCK_THUMB_HOVER_COLOR : THUMB_HOVER_COLOR};
  }
  &:active {
    background-color: ${stuck ? STUCK_THUMB_HOVER_COLOR : THUMB_HOVER_COLOR};
    cursor: grabbing;
  }
`
